import { isDefined } from "@clipboard-health/util-ts";
import { Box, CircularProgress, Dialog, DialogContent, ThemeProvider } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { berlinTheme } from "@src/appV2/redesign/theme/theme";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { type Facility } from "@src/appV2/User/api/useSearchFacilities";
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

import { type WorkerShift } from "./api/useGetWorkerShifts";
import { type WorkerStripeAccount } from "./api/useGetWorkerStripeAccount";
import { type WorkplaceShift } from "./api/useGetWorkplaceShifts";
import { type ShiftSearchResult } from "./api/useSearchShift";
import { KeyboardHints } from "./components/KeyboardHints";
import { OmnisearchInput } from "./components/OmnisearchInput";
import { type ShiftAction } from "./components/ShiftActionItem";
import { useOmnisearchKeyboard } from "./hooks/useOmnisearchKeyboard";
import { useOmnisearchResults } from "./hooks/useOmnisearchResults";
import { useOmnisearchSelect } from "./hooks/useOmnisearchSelect";
import { OmnisearchResultsList } from "./OmnisearchResultsList";

interface OmnisearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectAgent: (agent: Agent) => void;
  onSelectFacility: (facility: Facility) => void;
  onSelectShift: (shift: ShiftSearchResult) => void;
  onSelectShiftAction: (shift: ShiftSearchResult, action: ShiftAction) => void;
  onSelectAgentShift: (workerShift: WorkerShift) => void;
  onSelectAgentStripe: (stripeAccount: WorkerStripeAccount) => void;
  onSelectWorkplaceShift: (workplaceShift: WorkplaceShift) => void;
}

export function OmnisearchDialog(props: OmnisearchDialogProps) {
  const {
    open,
    onClose,
    onSelectAgent,
    onSelectFacility,
    onSelectShift,
    onSelectShiftAction,
    onSelectAgentShift,
    onSelectAgentStripe,
    onSelectWorkplaceShift,
  } = props;
  const [searchInput, setSearchInput] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expandedShiftId, setExpandedShiftId] = useState<string | null>(null);
  const [expandedWorkerId, setExpandedWorkerId] = useState<string | null>(null);
  const [expandedWorkplaceId, setExpandedWorkplaceId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousShiftIdRef = useRef<string | null>(null);

  const {
    agents,
    facilities,
    shift,
    shiftData,
    workerShifts,
    workerStripeAccount,
    workplaceShifts,
    allResults,
    isLoading,
    isLoadingWorkerShifts,
    isLoadingWorkerStripeAccount,
    workerHasShifts,
    workerHasStripeAccount,
    isLoadingWorkplaceShifts,
    workplaceHasShifts,
    hasResults,
    hasSearchInput,
    debouncedSearchInput,
  } = useOmnisearchResults({ searchInput, expandedShiftId, expandedWorkerId, expandedWorkplaceId });

  const resetState = useCallback(() => {
    setSearchInput("");
    setSelectedIndex(0);
    setExpandedShiftId(null);
    setExpandedWorkerId(null);
    setExpandedWorkplaceId(null);
    previousShiftIdRef.current = null;
  }, []);

  const handleSelect = useOmnisearchSelect({
    onSelectAgent,
    onSelectFacility,
    onSelectShift,
    onSelectShiftAction,
    onSelectAgentShift,
    onSelectAgentStripe,
    onSelectWorkplaceShift,
    onClose,
    resetState,
  });

  const { handleKeyDown, copiedItem } = useOmnisearchKeyboard({
    allResults,
    selectedIndex,
    setSelectedIndex,
    setExpandedShiftId,
    setExpandedWorkerId,
    setExpandedWorkplaceId,
    onSelect: handleSelect,
  });

  useEffect(() => {
    setSelectedIndex(0);
    setExpandedWorkerId(null);
    setExpandedWorkplaceId(null);
  }, [debouncedSearchInput]);

  useEffect(() => {
    if (isDefined(shiftData) && shiftData.id !== previousShiftIdRef.current) {
      previousShiftIdRef.current = shiftData.id;
      setExpandedShiftId(shiftData.id);
    }
  }, [shiftData]);

  const handleClose = useCallback(() => {
    onClose();
    resetState();
  }, [onClose, resetState]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  return (
    <ThemeProvider theme={berlinTheme}>
      <Dialog
        fullWidth
        maxWidth="sm"
        open={open}
        PaperProps={{
          sx: { position: "fixed", top: "15%", margin: "0", borderRadius: 2, maxHeight: "70vh" },
        }}
        onClose={handleClose}
      >
        <DialogContent sx={{ p: "8px" }}>
          <OmnisearchInput
            inputRef={inputRef}
            value={searchInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          {hasSearchInput && (
            <Box sx={{ borderTop: 1, borderColor: "divider" }}>
              {isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!isLoading && !hasResults && (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <Text color="text.secondary">
                    No results found for &quot;{debouncedSearchInput}&quot;
                  </Text>
                </Box>
              )}
              {!isLoading && hasResults && (
                <>
                  <Box sx={{ maxHeight: "50vh", overflowY: "auto" }}>
                    <OmnisearchResultsList
                      agents={agents}
                      copiedItem={copiedItem}
                      expandedShiftId={expandedShiftId}
                      expandedWorkerId={expandedWorkerId}
                      expandedWorkplaceId={expandedWorkplaceId}
                      facilities={facilities}
                      isLoadingWorkerShifts={isLoadingWorkerShifts}
                      isLoadingWorkerStripeAccount={isLoadingWorkerStripeAccount}
                      isLoadingWorkplaceShifts={isLoadingWorkplaceShifts}
                      selectedIndex={selectedIndex}
                      shift={shift}
                      workerHasShifts={workerHasShifts}
                      workerHasStripeAccount={workerHasStripeAccount}
                      workerShifts={workerShifts}
                      workerStripeAccount={workerStripeAccount}
                      workplaceHasShifts={workplaceHasShifts}
                      workplaceShifts={workplaceShifts}
                      onSelect={handleSelect}
                    />
                  </Box>
                  <Box
                    sx={(theme) => ({
                      paddingTop: 2,
                      paddingBottom: 1.5,
                      paddingX: 2,
                      borderTop: 1,
                      borderColor: "divider",
                      display: "flex",
                      justifyContent: "center",
                      gap: 2,
                      "& > *:not(:last-child)": {
                        borderRight: 1,
                        borderColor: theme.palette.divider,
                        paddingRight: 2,
                      },
                    })}
                  >
                    <KeyboardHints />
                  </Box>
                </>
              )}
            </Box>
          )}
          {!hasSearchInput && (
            <Box sx={{ py: 3, px: 2, borderTop: 1, borderColor: "divider" }}>
              <Text color="text.secondary" sx={{ textAlign: "center" }} variant="body2">
                Start typing to search for professionals or workplaces
              </Text>
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2 }}>
                <KeyboardHints />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}
